<template>
    <div>
        <hts-card
            class="mt-5"
            title="Hoá đơn">
            <hts-data-table
                ref="dataTableInvoice"
                class="max-h-[500px] min-h-[500px]"
                :headers="invoceHeaders"
                :server="serverInvoce"
                use-selection
                :use-archive="useInvoiceArchive"
                store-name="tripDataTable"
                store-key="Invoice" />
        </hts-card>

        <DeleteInvoice ref="deleteInvoice" />

        <DetailInvoice ref="detailInvoice" />

        <CreateReceiptRecord
            ref="createReceiptRecord"
            @refreshTables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import CreateReceiptRecord from './create-receipt-record.vue'
import DeleteInvoice from './delete-invoice.vue'
import DetailInvoice from './detail-invoice.vue'
import layoutPermissions from '@/data/permissions/layout-permissions'
import Status from '~/components/status.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
const { $formatters } = useNuxtApp()

const props = defineProps({
  trip: Object
})

const { trip } = toRefs(props)

const dataTableInvoice = ref()
const createInvoice = ref()
const createReceiptRecord = ref()
const deleteInvoice = ref()
const detailInvoice = ref()

const openCreateInvoice = (data: any) => {
  createInvoice.value && createInvoice.value.open(trip?.value?._id, data?._id)
}

const handleCreateInvoice = () => {
  createInvoice.value && createInvoice.value.open(trip?.value?._id)
}

const openDetailInvoice = (data: any) => {
  detailInvoice.value && detailInvoice.value.open(trip?.value?._id, data?._id)
}

const openCreateReceiptRecord = (data: any) => {
  createReceiptRecord.value && createReceiptRecord.value.open(trip?.value?._id, data)
}

const checkSentStatus = (debit: any) => {
  const status = debit.status

  if (['sent'].includes(status)) {
    return true
  }

  return false
}

const openDelete = (debit: any) => {
  if (!debit) {
    return null
  }

  deleteInvoice.value?.open(debit._id)
}

const invoceHeaders = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'customerName',
    headerName: 'Tên công ty',
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'selectInvoicingInformationName',
    headerName: $t('labels.invoicingInformation'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'invoicedDate',
    headerName: $t('labels.invoicedDate'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.invoicedDate)
    }
  },
  {
    field: 'dueDate',
    headerName: $t('labels.dueDate'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.dueDate)
    }
  },
  {
    field: 'totalAmount',
    headerName: $t('labels.totalAmount'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.totalAmount)
    }
  },
  {
    field: 'paidAmoutTotal',
    headerName: $t('labels.paidAmoutTotal'),
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.paidAmoutTotal)
    }
  },
  {
    field: 'currency',
    headerName: $t('labels.currency'),
    sortable: true,
    filter: 'agDateColumnFilter'
  },
  {
    field: 'paymentMethod',
    headerName: $t('labels.paymentMethod'),
    sortable: true,
    valueGetter: (params: any) => {
      const paymentMethod = params.data.paymentMethod
      if (paymentMethod === 'cash') {
        return 'Tiền mặt'
      }
      if (paymentMethod === 'bank') {
        return 'Chuyển khoản'
      }
      return ''
    }
  },
  {
    field: 'bankAccountInfo',
    headerName: $t('labels.bankAccount'),
    sortable: true,
    filter: 'agDateColumnFilter'
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'invoiceStatus',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: 'Chờ xác nhận', value: 'pending', class: { '!bg-yellow-100': true, '!text-yellow-800': true } },
      { name: 'Xác nhận', value: 'active', class: { '!bg-success-100': true, '!text-success-800': true } },
      { name: 'Đã gửi', value: 'sent', class: { '!bg-blue-100': true, '!text-blue-800': true } },
      { name: 'Thanh toán một phần', value: 'partial', class: { '!bg-purple-100': true, '!text-purple-800': true } },
      { name: 'Đã thanh toán', value: 'paid', class: { '!bg-green-100': true, '!text-green-800': true } },
      { name: 'Quá hạn', value: 'overdue', class: { '!bg-red-100': true, '!text-red-800': true } },
      { name: 'Quá hạn một phần', value: 'partialOverdue', class: { '!bg-orange-100': true, '!text-orange-800': true } }
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'tripDataTable',
      storeName: 'Invoice',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          click: openDetailInvoice
        },
        {
          icon: 'hi-eye-icon',
          label: $t('labels.createReceiptRecord'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          click: openCreateReceiptRecord
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.driver_delete),
          click: openDelete,
          validate: checkSentStatus,
          isArchive: true
        }
      ]
    }
  }
])

const serverInvoce = computed(() => ({
  url: `/trip/${trip?.value?._id}/billing-and-costing/invoice/list`,
  method: 'post'
}))

const refreshTables = () => {
  if (dataTableInvoice.value) {
    dataTableInvoice.value.refresh()
  }
}

const useInvoiceArchive = {
  api: {
    url: `/trip/${trip?.value?._id}/billing-and-costing/invoice/archive`,
    method: 'delete'
  },
  permissions: checkPermission(layoutPermissions.trip_management)
}

defineExpose({ refreshTables })
</script>
